import { useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type { BulkMovement, MovementBulkResponse } from '@/types/movement'

function parseBulkRow(row: MovementBulkResponse['data'][number]): BulkMovement {
    return {
        id: row[0],
        user: row[1],
        latitude: row[2],
        longitude: row[3],
        heading: row[4],
        speed: row[5],
        movement_type: row[6],
        is_moving: row[7],
        timestamp: row[8],
    }
}

interface UseMovementBulkLoadOptions {
    orgSlug?: string
    startDate: string
    endDate: string
    enabled?: boolean
}

export function useMovementBulkLoad({
    orgSlug,
    startDate,
    endDate,
    enabled = true,
}: UseMovementBulkLoadOptions) {
    return useQuery({
        queryKey: ['movement-bulk', orgSlug, startDate, endDate],
        queryFn: async () => {
            const response = await fetch(
                `${pb.baseURL}/api/movement/bulk?orgSlug=${encodeURIComponent(orgSlug ?? '')}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
                {
                    headers: {
                        Authorization: pb.authStore.token,
                    },
                }
            )

            if (!response.ok) {
                const error = await response.json()
                throw new Error(error.message || 'Failed to fetch movement data')
            }

            const result: MovementBulkResponse = await response.json()
            return result.data.map(parseBulkRow)
        },
        enabled: enabled && !!orgSlug && !!startDate && !!endDate,
        staleTime: 5 * 60 * 1000,
    })
}
